import React, { Component, ErrorInfo, ReactNode } from 'react';
import { FaExclamationTriangle } from 'react-icons/fa';
import Button from './Button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
    });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-paper p-4">
          <div className="max-w-md w-full bg-chalk border border-stone p-8 text-center">
            <FaExclamationTriangle className="text-error text-5xl mx-auto mb-4" />
            <h1 className="font-display text-2xl font-semibold text-ink mb-2">Something went wrong</h1>
            <p className="font-body text-ash mb-6">
              We hit an unexpected error. Try reloading the page.
            </p>
            {this.state.error && (
              <details className="text-left mb-6 text-sm text-ash">
                <summary className="cursor-pointer mb-2 font-mono text-xs">Error details</summary>
                <pre className="overflow-auto p-2 bg-paper border border-stone text-xs font-mono text-ink">
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
            <Button variant="primary" onClick={this.handleReset}>
              Reload page
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

